import { RootState } from "@/store";
import { CartItem, CartState } from "@/types/cart";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-hot-toast";

const initialState: CartState = {
  items: [],
  branchId: null,
  serviceFee: 0,
  taxRate: 0,
  hasServiceInCart: false,
};

const generateUniqueId = (item: CartItem) => {
  const optionsString = Object.entries(item.selected_options)
    .map(([key, option]) => `${key}-${option.quantity}-${option.surcharge}`)
    .join("|");
  return `${item.id}-${optionsString}`;
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<CartItem>) => {
      const newItem = action.payload;
      const uniqueId = generateUniqueId(newItem);
      const existingItem = state.items.find(
        (item) => item.uniqueId === uniqueId,
      );
      const differentProductType = state.items.some(
        (item) => item.product_type !== newItem.product_type,
      );

      // Check limit_per_order for daily specials
      if (newItem.limit_per_order) {
        // Calculate total quantity of this product already in cart (by product ID)
        const currentQuantityInCart = state.items
          .filter((item) => item.id === newItem.id)
          .reduce((total, item) => total + item.quantity, 0);

        const totalAfterAdd = currentQuantityInCart + newItem.quantity;

        if (totalAfterAdd > newItem.limit_per_order) {
          const remaining = newItem.limit_per_order - currentQuantityInCart;
          if (remaining <= 0) {
            toast.error(
              `Maximum ${newItem.limit_per_order} of this special allowed per order`,
            );
            return;
          } else {
            toast.error(
              `You can only add ${remaining} more of this special (limit ${newItem.limit_per_order} per order)`,
            );
            return;
          }
        }
      }

      if (newItem.product_type === "Service" && state.items.length > 0) {
        state.items = [];
        state.items.push({ ...newItem, uniqueId });
        state.hasServiceInCart = true;
        return;
      } else if (differentProductType) {
        toast.error("You can only add Physical Products or Services at a time");
        return;
      }

      if (existingItem) {
        existingItem.quantity += newItem.quantity;
      } else {
        state.items.push({ ...newItem, uniqueId });
      }
      state.hasServiceInCart = state.items.some(
        (item) => item.product_type === "Service",
      );
    },

    removeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(
        (item) => item.uniqueId !== action.payload,
      );
      state.hasServiceInCart = state.items.some(
        (item) => item.product_type === "Service",
      );
    },

    updateHasServiceInCart: (state) => {
      const hasService = state.items.some(
        (item) => item.product_type === "Service",
      );
      if (state.hasServiceInCart !== hasService) {
        state.hasServiceInCart = hasService;
      }
    },

    updateItemQuantity: (
      state,
      action: PayloadAction<{
        itemId: string;
        quantity: number;
      }>,
    ) => {
      const { itemId, quantity } = action.payload;
      const item = state.items.find((item) => item.uniqueId === itemId);
      if (item) {
        // Check limit_per_order for daily specials
        if (item.limit_per_order) {
          // Calculate total quantity of this product in cart (excluding current item)
          const otherItemsQuantity = state.items
            .filter((i) => i.id === item.id && i.uniqueId !== itemId)
            .reduce((total, i) => total + i.quantity, 0);

          const totalAfterUpdate = otherItemsQuantity + quantity;

          if (totalAfterUpdate > item.limit_per_order) {
            toast.error(
              `Maximum ${item.limit_per_order} of this special allowed per order`,
            );
            return;
          }
        }
        item.quantity = quantity;
      }
      state.hasServiceInCart = state.items.some(
        (item) => item.product_type === "Service",
      );
    },
    clearCart: (state) => {
      state.items = [];
      state.hasServiceInCart = false;
    },
    setBranchId: (state, action: PayloadAction<string | null>) => {
      state.branchId = action.payload;
    },
    setServiceFee: (state, action: PayloadAction<number>) => {
      state.serviceFee = action.payload;
    },
    setTaxRate: (state, action: PayloadAction<number>) => {
      state.taxRate = action.payload;
    },
  },
});

export const {
  addItem,
  removeItem,
  updateHasServiceInCart,
  updateItemQuantity,
  clearCart,
  setBranchId,
  setServiceFee,
  setTaxRate,
} = cartSlice.actions;

export const validateCart = (state: RootState) => {
  return state.cart.items.filter(
    (item) => item.branch !== Number(state.cart.branchId),
  ).length;
};
export const selectCartItemCount = (state: RootState) => {
  if (validateCart(state)) {
    return 0;
  }
  return state.cart.items.reduce((total, item) => total + item.quantity, 0);
};

export default cartSlice.reducer;
